/**
 * Whether a mention in an answer to a prompt is evidence of visibility.
 *
 * The backend classes every detection sample as unaided, aided or unclassified (PresenceClass) and
 * never pools them into one rate. This file carries the words for that split, in one place, because
 * the whole point of the split is that the two numbers must never be read as the same measurement.
 *
 * Keys are the camelCase values the API sends — the backend's global
 * ConditionalJsonStringEnumConverter(CamelCase) serialises every enum this way.
 */

export const PRESENCE_CLASSES = {
  unaided: {
    label: "Prompts that do not name you",
    shortLabel: "Unaided",
    color: "green",
    /** Shown under the headline number. */
    meaning:
      "The buyer's question never mentions your brand, so a mention means the model brought you " +
      "up on its own. This is the number that measures being found.",
  },
  aided: {
    label: "Prompts that name you",
    shortLabel: "Aided",
    color: "purple",
    meaning:
      "The question already contains your brand name, so the answer repeats it back. Near 100% " +
      "is the expected reading and says nothing about visibility. A low reading here is a signal " +
      "about the surface or a missing spelling, not about how well known you are.",
  },
  unclassified: {
    label: "Prompts that no longer exist",
    shortLabel: "Unclassified",
    color: "gray",
    meaning:
      "The prompt was deleted and nothing left on record says whether it named your brand, so " +
      "these samples are kept out of both numbers rather than guessed into one.",
  },
};

/** Reading order. Unaided first because it is the only one that answers "are we found at all". */
export const PRESENCE_CLASS_ORDER = ["unaided", "aided", "unclassified"];

/** Metadata for a class, including one this build has never heard of. */
export function presenceClassMeta(value) {
  if (!value) return null;
  return (
    PRESENCE_CLASSES[value] ?? {
      label: value,
      shortLabel: value,
      color: "gray",
      meaning: "This grouping is newer than this page. The samples are still being counted.",
    }
  );
}
